import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import gql from 'graphql-tag';
import { v4 as uuid } from 'uuid';
import { useQuery } from '@apollo/client';
import queryString from 'query-string';

import * as actions from '~actions';
import { useDispatch, useLogger, useSelector, useAdyen } from '~Hooks';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import { useStorage } from '~Components/Storage';
import { VenueData, VenueDataVariables } from './__queries__';
import { getCompany_Venue_company_staffs } from '~Scenes/Venue/__queries__';
import AdyenTerminalComms from '@baemingo/adyen-terminal-comms';

const adyenKey = process.env.ADYEN_KEY!;
const adyenPass = process.env.ADYEN_PASS!;

interface EncryptedAdyenTerminalEvent {
  SaleToPOIRequest: {
    MessageHeader: {
      DeviceID: string;
      MessageCategory: string;
      MessageClass: string;
      MessageType: 'Notification';
      POIID: string;
      ProtocolVersion: string;
      SaleID: string;
      ServiceID: string;
    };
    NexoBlob: string;
    SecurityTrailer: {
      AdyenCryptoVersion: number;
      Hmac: string;
      KeyIdentifier: string;
      KeyVersion: number;
      Nonce: string;
    };
  };
}

interface AdyenTerminalEvent {
  SaleToPOIRequest: {
    EventNotification: {
      EventDetails: string;
      EventToNotify: string;
      TimeStamp: string;
    };
    MessageHeader: {
      DeviceID: string;
      MessageCategory: string;
      MessageClass: string;
      MessageType: 'Notification';
      POIID: string;
      ProtocolVersion: string;
      SaleID: string;
    };
  };
}

type TerminalEvent = AdyenTerminalEvent | EncryptedAdyenTerminalEvent;

const securityKey = {
  adyenCryptoVersion: 1,
  keyIdentifier: adyenKey,
  keyVersion: 0,
  passphrase: adyenPass,
};

const DATA_QUERY = gql`
  query VenueData($id: ID!) {
    venue: Venue(id: $id) {
      id
      name
      organizationId

      hasAdyenConfiguration

      tables {
        id
        code
      }

      company {
        id
        name
        defaultCurrency
      }

      location {
        id
        name
        city
        street
        zip
      }
    }
  }
`;

type check = {
  name: string;
  id: string;
  amount?: number;
};

const PayAtTable = () => {
  const initialTableDataLoaded = !!useSelector((state) => state.lastTableEventData);
  const POSMenuLoaded = Boolean(useSelector((state) => state.POSMenu));

  const [terminalStatus, setTerminalStatus] = useState<{
    [key: string]: number;
  }>({});
  const [, , venueId] = location.hash.split('/');
  const [payState, setPayState] = useState<TerminalEvent | string | null>(null);
  const api = useTableApi();
  const adyen = useAdyen();
  const dispatch = useDispatch();
  const logger = useLogger('pay-at-table');
  const staffCodes = useSelector((state) => state.companyStaff?.staffs ?? []);
  const universe = useTableUniverse();
  const [storage] = useStorage();

  const { adyenTerminals } = storage;
  const allCheques = universe.cheques;
  const hasStaffCodes = staffCodes.length > 0;

  const { data } = useQuery<VenueData, VenueDataVariables>(DATA_QUERY, {
    variables: {
      id: venueId,
    },
    skip: !venueId,
  });

  const isCloudConfigured = !!data?.venue?.hasAdyenConfiguration;

  // Get all open tables - based on the open cheques
  const tables = universe.cheques.reduce(
    (arr, cheque) => {
      // If the cheque is at a table and we havent already added this table to our array of tables
      if (cheque.tableId && !arr.some((t) => t.id === cheque.tableId)) {
        // Find the table in the data from backend
        const table = data?.venue?.tables.find((t) => t.id === cheque.tableId);

        // This can be false in the scenario that our backend query fails. Should probably be handled in a better way
        if (table) {
          arr.push({
            id: cheque.tableId,
            code: table.code,
          });
        }
      }
      return arr;
    },
    [] as Array<{
      id: string;
      code: string;
    }>,
  );

  const onMessage = (message: any) => {
    let data = message.data;

    try {
      data = JSON.parse(message.data);
    } catch (e) {} // eslint-disable-line

    if (data.type === 'ADYEN_EVENT') {
      setPayState(data.message);
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage);

    /* return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage);
    };*/
  }, []);

  const findTerminalIP = (poiid: string, loggerRequestId: string) => {
    const result = adyenTerminals.find((item) => item.POIID === poiid);

    return result ? result.IP : null;
  };

  const findCheques = (tableId: string, staffId?: string) => {
    return universe.cheques
      .filter((cheque) =>
        staffId
          ? cheque.owner === staffId && cheque.tableId === tableId
          : cheque.tableId === tableId,
      )
      .filter((cheque) => cheque.amount !== 0)
      .map((c) => ({
        ...c,
        name: c.name || c.id.substr(0, 4).toUpperCase(),
      }));
  };

  const openTables = tables.filter((table) =>
    allCheques.some((item) => item.tableId === table.id),
  );

  const handleBridgeEvent = async (messageFromBridge: TerminalEvent | string) => {
    let terminal: AdyenTerminalComms;

    let referenceId: string;

    // If we are force-starting it in dev
    if (typeof messageFromBridge === 'string') {
      referenceId = messageFromBridge;
      terminal = adyen.createTerminalInstance(adyenTerminals[0].POIID);
    } else {
      const request = messageFromBridge.SaleToPOIRequest;
      let type;
      let params: {
        [key: string]: string;
      };

      const isDisplayRequest = request.MessageHeader.MessageCategory !== 'Event';
      const isEncrypted = 'NexoBlob' in request;

      if (isDisplayRequest) {
        return;
      }

      /**
       * If it's encrypted, decrypt it. Otherwise, simply parse out the values
       */
      if (isEncrypted) {
        terminal = adyen.createTerminalInstance();
        const decryptedResult = await terminal.parse(securityKey, request);
        type = decryptedResult.type;
        params = decryptedResult.params;
      } else {
        terminal = adyen.createTerminalInstance(request.MessageHeader.POIID);
        console.log(request);
        type = request.EventNotification.EventToNotify;
        params = queryString.parse(request.EventNotification.EventDetails) as any;
      }

      if (type === 'Initialised') {
        setTerminalStatus({
          ...terminalStatus,
          [terminal.senderPoiid!]: Math.random(),
        });
      }

      if (type !== 'SaleWakeUp') {
        logger.warn('Not SaleWakeUp, skipping', {
          payload: {
            type,
          },
        });
        return;
      }

      referenceId = params.reference_id;
    }

    // Since following the trail of a single event is important,
    // we will assign a unique ID to all logs in this handler
    const requestId = uuid();

    logger.info('progressed', {
      requestId,
    });

    if (!referenceId) {
      terminal.displayDeclinedPayment();
      throw new Error('No reference ID set');
    }

    logger.info('Reference ID set', {
      requestId,
    });

    // Find the ID
    const poiid = terminal.senderPoiid;

    if (!poiid) {
      terminal.displayDeclinedPayment();

      throw new Error('No POIID found');
    }

    logger.info('Terminal ID set', {
      requestId,
    });

    // Find the terminal that made the request
    const terminalIP = findTerminalIP(poiid, requestId);

    if (!terminalIP && !isCloudConfigured) {
      // Display this as an error that has to be manually removed, very important info
      dispatch(actions.addToast(`Pay at table: Terminal ${poiid} not found`, false, null, true));

      logger.warn('Terminal not found', {
        requestId,
        terminalId: poiid,
      });

      throw new Error('No IP found for the device');
    }

    logger.info('Terminal found. ID: ' + poiid, {
      requestId,
    });

    // Set the IP so we know where to send our subsequent requests
    if (terminalIP) {
      terminal.senderIp = terminalIP;
    }

    // If table data or pos menu hasn't finished loading, abort mission
    if (!initialTableDataLoaded) {
      await terminal.prompt('Table data is still loading, please try again', [
        { id: 'ok', title: 'Ok' },
      ]);
      logger.warn('Aborting, table data not loaded yet', {
        requestId,
        terminalId: poiid,
      });
      return;
    }

    if (!POSMenuLoaded) {
      await terminal.prompt('Menu items are still loading, please try again', [
        { id: 'ok', title: 'Ok' },
      ]);
      logger.warn('Aborting, POS menu not loaded yet', {
        requestId,
        terminalId: poiid,
      });
      return;
    }

    let staff: getCompany_Venue_company_staffs | undefined;
    let tableId: string | null = null;
    let tableName: string | null = null;

    // If we have staff codes, the terminal is configured
    // to input staff ID instead of table code on "wake up" prompt
    if (hasStaffCodes) {
      logger.info('Using staff codes', {
        requestId,
      });

      // Find the staff
      staff = staffCodes.find((code) => code.code === referenceId);

      // If no one was found for this code, abort
      if (!staff) {
        // Let the user know their request was incorrect
        await terminal.prompt('Staff not found', [{ id: 'ok', title: 'Ok' }]);
        logger.warn('Aborting, staff not found', {
          requestId,
          terminalId: poiid,
        });
        return;
      }

      logger.info('Staff found', {
        requestId,
      });

      // If staff doesn't have permission to make payments, abort
      const staffPermissions = staff.accessLevel.permissions;
      if (!staffPermissions.some((permission) => permission === 'MAKE_PAYMENT')) {
        await terminal.prompt('Staff code not allowed to make payments', [
          { id: 'ok', title: 'Ok' },
        ]);
        logger.warn('Aborting, staff not allowed to make payments', {
          requestId,
          terminalId: poiid,
        });
        return;
      }

      // Get tables with open cheques owned by staff
      const staffTables = tables.filter((table) =>
        allCheques.some((cheque) => cheque.owner === staff?.id && cheque.tableId === table.id),
      );

      // If no open tables, abort
      if (staffTables.length === 0) {
        await terminal.prompt('You have no open tables', [{ id: 'ok', title: 'Ok' }]);
        logger.warn('Aborting, staff has no open tables', {
          requestId,
          terminalId: poiid,
        });
        return;
      }

      // Map tables to format for prompting
      const tablesForPrompt = staffTables
        .map((t) => ({
          id: t.id,
          title: `Table ${t.code}`,
        }))
        .sort((a, b) => (a.title < b.title ? -1 : 1))
        .slice(0, 14);

      // Present a display that has a "search" option
      // at the top, and then table numbers below
      tableId = await terminal.prompt('Select', [
        {
          id: 'search',
          title: 'Search',
        },
        ...tablesForPrompt,
      ]);

      logger.info(tableId, {
        requestId,
      });

      // If user pressed "search"
      if (tableId === 'search') {
        tableId = null;

        // Ask the user to input table code
        const tableCode = await terminal.promptNumber('Enter table number');

        // Look up the table
        const table = staffTables.find((t) => t.code === tableCode);
        // If table is found, assign it
        if (table) {
          tableId = table.id;
          tableName = tableCode;
        }
      } else {
        tableName = tablesForPrompt.find((t) => t.id === tableId)?.title || null;
      }
    } else {
      // Look up the table
      const table = openTables.find((t) => t.code === referenceId);
      if (table) {
        tableId = table.id;
        tableName = table.code;
      }
    }
    // } catch (e) {
    //   await terminal.prompt('Error', [
    //     {
    //       id: 'ok',
    //       title: 'OK',
    //     },
    //   ]);
    //   throw e;
    // }

    if (!tableId) {
      // Let the user know their request was incorrect
      await terminal.prompt('Table not active', [{ id: 'ok', title: 'Ok' }]);
      logger.warn('Aborting, couldnt find table', {
        requestId,
        terminalId: poiid,
      });
      return;
    }

    logger.info(`Table: ${tableName}`, {
      requestId,
    });

    // Find cheques for this table
    const cheques = findCheques(tableId, staff?.id);

    let cheque = cheques[0];

    // If all cheques on this table are empty
    if (!cheque) {
      // Let the user know their request was incorrect
      await terminal.prompt('Table not active', [{ id: 'ok', title: 'Ok' }]);
      logger.warn('Aborting, no open cheques on table', {
        requestId,
        terminalId: poiid,
      });
      return;
    }

    // If there's more than one cheque at the table, let them pick
    if (cheques.length > 1) {
      const id = await terminal.prompt(
        'Pick cheque',
        cheques.map((c) => ({
          id: c.id,
          title: `${c.name}: ${((c.amount - c.discount - c.paidAmount) / 100).toFixed(2)}`,
        })),
      );

      cheque = cheques.find((c) => c.id === id)!;
    }

    // Ask the user what they wish to do
    // Disabled for now - but let's leave the code in
    /*
    const selection = await terminal.prompt('', [
      {
        id: 'print',
        title: 'Print Cheque',
      },
      {
        id: 'pay',
        title: 'Pay',
      },
    ]);

    // The user made a choice
    switch (selection) {
      case 'pay':
        // Start a payment
        api.initiatePayment(cheque.id, null, 'ADYEN');
        break;
      case 'print':
        // Print the chosen cheque
        terminal.printCheck(
          cheque.amount - cheque.paidAmount,
          {
            items: universe.items
              .filter((item) => item.chequeId === cheque.id)
              .map((item) => ({
                title: item.title,
                amount: item.amount - item.discount,
              })),
          },
          data!.venue!,
        );
        break;
    }
    */

    setTerminalStatus({
      ...terminalStatus,
      [terminal.senderPoiid!]: Math.random(),
    });

    const total = cheque.amount - cheque.discount - cheque.paidAmount;

    const response = await terminal.promptNumber('Amount', Math.ceil(total / 100).toString());

    logger.info(`Initiating payment. Total: ${total / 100}, Input: ${response}`, {
      requestId,
    });

    const amount = response ? parseFloat(response) * 100 : total;
    api.initiatePayment(
      cheque.id,
      tableName,
      'ADYEN',
      Math.min(amount, total),
      poiid,
      staff?.id,
      undefined,
      undefined,
      cheque.bookingData?.externalId,
    );
    api.log.initiatePayment(cheque.id);
  };

  useEffect(() => {
    if (!payState) {
      return;
    }

    handleBridgeEvent(payState)
      .then((res) => {})
      .catch((err) => {
        logger.warn(err.message);
      });
  }, [payState]);

  useEffect(() => {
    // @ts-ignore
    window.payAtTable = (input: string) => {
      setPayState(input);
    };
  }, []);

  return null;
};

export default PayAtTable;
